import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { useAuth } from '../hooks/useAuth.jsx';
import { configurationAPI } from '../services/api';
import {
  applyUiThemeToDocument,
  clearUiThemeFromDocument,
  normalizeUiTheme,
  readCachedUiTheme,
  writeCachedUiTheme,
  DEFAULT_UI_THEME,
  UiTheme,
} from '../lib/uiTheme';

function extractUiTheme(response: unknown): UiTheme {
  const body = (response as { data?: { data?: { uiTheme?: unknown } } })?.data;
  return normalizeUiTheme(body?.data?.uiTheme);
}

export const TenantThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const tenantId = user?.tenantId || user?.tenant?.id || null;

  // Apply cached theme immediately to reduce flash
  useEffect(() => {
    if (!tenantId) {
      clearUiThemeFromDocument();
      return;
    }
    const cached = readCachedUiTheme(tenantId);
    if (cached) {
      applyUiThemeToDocument(cached);
    }
  }, [tenantId]);

  const { data } = useQuery(
    ['configuration-theme', tenantId],
    () => configurationAPI.getTheme(),
    {
      enabled: Boolean(tenantId),
      staleTime: 60_000,
      onSuccess: (response) => {
        if (!tenantId) return;
        const theme = extractUiTheme(response);
        writeCachedUiTheme(tenantId, theme);
        applyUiThemeToDocument(theme);
      },
      onError: () => {
        if (!tenantId) {
          clearUiThemeFromDocument();
          return;
        }
        const cached = readCachedUiTheme(tenantId);
        applyUiThemeToDocument(cached || DEFAULT_UI_THEME);
      },
    }
  );

  useEffect(() => {
    if (!tenantId) {
      clearUiThemeFromDocument();
      return;
    }
    if (data) {
      const theme = extractUiTheme(data);
      writeCachedUiTheme(tenantId, theme);
      applyUiThemeToDocument(theme);
    }
  }, [data, tenantId]);

  useEffect(() => {
    if (!user) {
      clearUiThemeFromDocument();
    }
  }, [user]);

  return <>{children}</>;
};

export default TenantThemeProvider;
